/**
 * Auth state hook for AiPrompt.si V1.0.
 *
 * - On mount: GET /api/auth/me to populate user + quota.
 * - signup / login: POST credentials, server sets cookies, we just refresh.
 * - logout: POST with CSRF header, then refresh.
 * - CSRF token is read from the cookie (double-submit) and echoed in
 *   the X-CSRF-Token header on every mutation.
 */
import { useCallback, useEffect, useState } from 'react';

export type Role = 'GUEST' | 'SUBSCRIBER' | 'ADMIN';

export interface AuthUser {
  id: string;
  email: string;
  role: Role;
  subscriptionStatus: string | null;
  subscriptionEndsAt: string | null;
  emailVerifiedAt: string | null;
  inviteBonusType: string | null;
  /** True once the user has checked out via Stripe at least once.
   *  False for invite-trial subscribers who have no Stripe customer record
   *  and therefore cannot use the "Manage billing" portal. */
  hasStripeCustomer: boolean;
  /** Orthogonal "free forever" flag — independent of role/Stripe. Auto-
   *  granted on signup for @unstoppabledomains.com emails, toggleable
   *  per-user from the admin panel. When true, all paid-tier features
   *  are unlocked regardless of role. */
  lifetimeFree: boolean;
  /** Last 4 chars of the user's stored Unstoppable Domains MCP key,
   *  or null if no key is on file. The full key never leaves the
   *  server; this masked tail powers the "set / not set" display in
   *  Settings and the UD-bulk-check button enable state. */
  udMcpApiKeyLast4: string | null;
  createdAt: string;
}

export interface Quota {
  tier: 'ANON' | 'GUEST' | 'SUBSCRIBER' | 'ADMIN';
  limit: number | null; // null = unlimited
  used: number | null;
  remaining: number | null;
  resetsAt: string | null;
}

interface ApiError {
  error: { code: string; message: string };
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

async function api<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = readCookie('aiprompt_csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  const res = await fetch('/api' + path, {
    method,
    headers,
    credentials: 'same-origin',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const err = (data as ApiError | null)?.error;
    throw new Error(err?.message || `Request failed (${res.status})`);
  }
  return data as T;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [quota, setQuota] = useState<Quota | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api<{ user: AuthUser | null; quota: Quota }>('GET', '/auth/me');
      setUser(data.user);
      setQuota(data.quota);
    } catch (err) {
      // Network blip — keep last known state rather than crash the UI.
      console.warn('[auth] refresh failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signup = useCallback(
    async (email: string, password: string, inviteCode?: string) => {
      await api('POST', '/auth/signup', {
        email,
        password,
        inviteCode: inviteCode || undefined,
      });
      await refresh();
    },
    [refresh],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      await api('POST', '/auth/login', { email, password });
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await api('POST', '/auth/logout');
    } finally {
      await refresh();
    }
  }, [refresh]);

  return { user, quota, loading, signup, login, logout, refresh };
}
